import { useCallback, useEffect, useRef, useState } from 'react';
import { bookingRoomLabel } from '../../utils/bookingRoomLabel';
import { bookingGuestNameLabel } from '../../utils/bookingGuestLabel';
import { agentCommandService, type AgentCommandResult } from '../../services/agentCommandService';
import { notifyBookingsChanged } from '../../services/bookingEvents';
import { usePropertySelection } from '../../hooks/usePropertySelection';
import { SidePanel, PanelContent, useCockpitPanel } from './SidePanel';
import { Kbd } from '../ui/Kbd';
import { HouseChip } from '../ui/HouseChip';
import { getTenantRuntime } from '../../config/tenant';

interface Exchange {
  question: string;
  context: { propertyKeys: string[]; previousCommand?: string };
  result?: AgentCommandResult;
  error?: string;
  loading: boolean;
  decision?: 'execute' | 'discard';
  proposalId?: string;
}

export function PaulaSession({ open, draft, trigger, onClose }: {
  open: boolean; draft: string; trigger: HTMLElement | null; onClose: () => void;
}) {
  const panel = useCockpitPanel();
  const { selectedProperties } = usePropertySelection();
  const [input, setInput] = useState('');
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [pendingCommand, setPendingCommand] = useState<string>();
  const busy = useRef(false);
  const sessionId = useRef(crypto.randomUUID());
  const sessionUsed = useRef(false);
  const questionInput = useRef<HTMLTextAreaElement>(null);
  const answer = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);
  const sessionWasOpen = useRef(false);
  const sessionOpen = panel ? !!panel.selection : open;
  const properties = getTenantRuntime().properties;
  const alive = useRef(true);
  const [sessionCloseError, setSessionCloseError] = useState<string>();
  const endSession = useCallback(() => {
    if (sessionUsed.current) {
      void agentCommandService.execute('', undefined, { sessionId: sessionId.current, decision: 'end_session' }).catch(() => {
        if (alive.current) setSessionCloseError('Die Sitzung konnte beim Dienst nicht beendet werden. Offene Vorschläge verfallen spätestens mit dem Ablauf der Anmeldung.');
      });
    }
    sessionUsed.current = false;
    sessionId.current = crypto.randomUUID();
  }, []);
  useEffect(() => { alive.current = true; return () => { alive.current = false; endSession(); }; }, [endSession]);
  useEffect(() => {
    if (open && !wasOpen.current && draft) setInput(current => current || draft);
    wasOpen.current = open;
  }, [open, draft]);
  useEffect(() => {
    if (!sessionOpen && sessionWasOpen.current) {
      endSession();
      setPendingCommand(undefined);
      setExchanges(current => current.map(exchange => exchange.loading
        ? { ...exchange, loading: false, result: { success: true, status: 'unsupported' as const, message: exchange.decision === 'execute' ? 'Unklar, ob ausgeführt wurde. Bitte prüfen, ob die Aufgabe angelegt ist, bevor du den Auftrag erneut stellst.' : 'Die Sitzung wurde während der Anfrage beendet. Bitte den gespeicherten Zustand vor einem neuen Auftrag prüfen.' } }
        : exchange.result?.status === 'proposed'
        ? { ...exchange, result: { success: true, status: 'discarded', message: 'Vorschlag mit dem Sitzungsende verworfen.' } }
        : exchange));
    }
    sessionWasOpen.current = sessionOpen;
  }, [sessionOpen, endSession]);
  useEffect(() => { if (open) answer.current?.scrollIntoView?.({ block: 'start' }); }, [exchanges, open]);
  const run = async (exchange: Exchange, index: number) => {
    if (busy.current) return;
    busy.current = true;
    setExchanges(current => { const next = [...current]; next[index] = { ...exchange, loading: true, error: undefined }; return next; });
    const currentSession = sessionId.current;
    sessionUsed.current = true;
    try {
      const result = await agentCommandService.execute(exchange.question, exchange.context,
        { sessionId: currentSession, ...(exchange.decision ? { decision: exchange.decision, proposalId: exchange.proposalId } : {}) });
      if (!alive.current || currentSession !== sessionId.current) return;
      setExchanges(current => current.map((item, i) => i === index ? { ...exchange, result, loading: false, error: undefined } : item));
      if (result.status === 'executed') { setPendingCommand(undefined); notifyBookingsChanged(); }
      else if (result.status === 'clarification_required') setPendingCommand(exchange.context.previousCommand || exchange.question);
    } catch (cause) {
      if (alive.current && currentSession === sessionId.current) {
        const code = (cause as Error & { code?: string })?.code;
        const reset = code === 'SESSION_CLOSED' || code === 'PROPOSAL_UNAVAILABLE';
        const message = reset ? 'Eine neue Sitzung wurde begonnen. Bitte stelle den Auftrag erneut.'
          : exchange.decision === 'execute' ? 'Unklar, ob ausgeführt wurde. Bitte prüfen, ob die Aufgabe angelegt ist, bevor du den Auftrag erneut stellst.'
          : cause instanceof Error ? cause.message : 'unbekannte Ursache';
        if (reset) { endSession(); setPendingCommand(undefined); }
        setExchanges(current => current.map((item, i) => i === index ? { ...exchange, decision: undefined, result: undefined, loading: false, error: message }
          : reset && item.result?.status === 'proposed' ? { ...item, result: { success: true, status: 'discarded', message: 'Vorschlag aus der vorherigen Sitzung verworfen.' } } : item));
      }
    } finally { busy.current = false; }
  };
  const send = () => {
    const question = input.trim();
    if (!question || busy.current || !selectedProperties.size) return;
    const exchange = { question, context: { propertyKeys: Array.from(selectedProperties), previousCommand: pendingCommand }, loading: true };
    setInput('');
    questionInput.current?.focus();
    void run(exchange, exchanges.length);
  };
  const sending = exchanges.some(item => item.loading);
  const content = <>
    <div className="flex h-full min-h-0 flex-1 flex-col gap-3">
      <div className="min-h-0 min-w-0 flex-1 overflow-auto space-y-3 [overflow-wrap:anywhere]" role="log" aria-label="Paula-Sitzungsverlauf" aria-live="polite" aria-relevant="additions text">
        {sessionCloseError && <p role="alert" className="text-sm text-crit">{sessionCloseError}</p>}
        {!exchanges.length && <p className="text-sm text-muted">Paula bereit</p>}
        {exchanges.map((exchange, index) => <article key={index} className="space-y-2 text-sm">
          <p className="whitespace-pre-wrap rounded-ctl bg-signal-soft p-3"><strong>Frage: </strong>{exchange.question}</p>
          <div ref={index === exchanges.length - 1 ? answer : undefined} className="rounded-ctl border border-line p-3">
            {exchange.loading ? <p>Paula antwortet …</p> : exchange.error ? <div className="text-crit"><p>{exchange.error.startsWith('Agent-Anfrage fehlgeschlagen') ? exchange.error : `Agent-Anfrage fehlgeschlagen: ${exchange.error}`}</p>{!exchange.decision && !exchange.proposalId && <button type="button" disabled={sending} className="mt-2 rounded-ctl border border-line px-2 py-1" onClick={() => { questionInput.current?.focus(); void run(exchange, index); }}>Erneut versuchen</button>}</div> : exchange.result && <>
              <p className={`whitespace-pre-wrap ${exchange.result.status === 'unsupported' ? 'text-crit' : exchange.result.status === 'clarification_required' ? 'text-warn' : 'text-ink'}`}><strong>Paula: </strong>{answerText(exchange.result)}</p>
              {exchange.result.result?.toolName === 'booking.timeline' && typeof exchange.result.result.result?.bookingId === 'string' && <a className="text-signal underline" href={`/bookings/${encodeURIComponent(exchange.result.result.result.bookingId)}`}>Buchung öffnen</a>}
              {exchange.result.status === 'proposed' && exchange.result.proposal && <div className="mt-2 space-y-2">
                <p>{exchange.result.proposal.description}</p>
                <details><summary>Technische Details</summary><p>{exchange.result.proposal.toolName}</p>
                <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(exchange.result.proposal.parameters, null, 2)}</pre></details>
                <div className="flex gap-2">{(['execute', 'discard'] as const).map(decision => <button key={decision} type="button" disabled={sending}
                  className="rounded-ctl border border-line px-3 py-2" onClick={() => void run({ ...exchange, decision, proposalId: exchange.result!.proposal!.id }, index)}>
                  {decision === 'execute' ? 'Ausführen' : 'Verwerfen'}
                </button>)}</div>
              </div>}
              {!!exchange.result.clarification?.options?.length && <ul className="mt-2 space-y-1">{exchange.result.clarification.options.map(option => <li key={option.bookingReference} className="rounded-cell border border-line p-2 text-xs"><strong>{option.bookingReference}</strong> · {bookingGuestNameLabel(option)}{option.roomNumber ? ` · ${bookingRoomLabel(option)}` : ''}{option.checkIn ? ` · ${option.checkIn}` : ''}</li>)}</ul>}
            </>}
          </div>
        </article>)}
      </div>
      <div className="space-y-2 border-t border-line pt-3">
        <div aria-label="Geltungsbereich" className="flex flex-wrap items-center gap-1 text-sm">
          {!selectedProperties.size ? <span role="status">Kein Standort gewählt</span> : <>
            <span>{properties.every(property => selectedProperties.has(property.key)) ? 'Alle Standorte' : 'Standorte:'}</span>
            {properties.filter(property => selectedProperties.has(property.key)).map(property => <HouseChip key={property.key} code={property.key} />)}
          </>}
        </div>
        <label htmlFor="paula-question" className="text-sm font-semibold">Weitere Frage an Paula</label>
        <textarea ref={questionInput} id="paula-question" value={input} onChange={event => setInput(event.target.value)}
          onKeyDown={event => { if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) { event.preventDefault(); send(); } }}
          placeholder="z. B. Zwischenreinigung Zimmer 12 auf morgen verschieben"
          className="min-h-[76px] w-full resize-y rounded-ctl border border-line bg-panel p-3 text-sm text-ink" />
        <div className="flex items-center justify-between gap-2"><span className="text-xs text-muted"><Kbd>⌘/Strg+Enter</Kbd> sendet</span><button type="button" title="Senden" disabled={sending || !input.trim() || !selectedProperties.size} onClick={send} className="rounded-ctl border border-line bg-ink px-3 py-2 text-sm text-panel disabled:opacity-50">Senden</button></div>
      </div>
    </div>
  </>;
  return panel ? <PanelContent id="paula">{content}</PanelContent> : <SidePanel open={open} title="Frag Paula" returnFocus={trigger} onClose={onClose} keepMounted>{content}</SidePanel>;
}

function answerText(result: AgentCommandResult): string {
  if (result.status === 'executed' && result.message && ['booking.timeline', 'dashboard.changes', 'cleaning.planFor', 'team.responsibleFor', 'availability.check', 'faq.lookup'].includes(result.result?.toolName || '')) return result.message;
  if (result.status === 'executed') return result.result?.result ? summarizeResult(result.result.toolName, result.result.result) : result.message || 'Auftrag ausgeführt.';
  return result.clarification?.question || result.message || (result.status === 'clarification_required' ? 'Bitte auswählen.' : 'Dieser Auftrag wird noch nicht unterstützt.');
}

function summarizeResult(toolName: string | undefined, result: Record<string, unknown>): string {
  if (typeof result.displayText === 'string' && result.displayText.trim()) {
    return result.displayText;
  }

  if (toolName === 'cleaningTask.ensureDailyMidstay') {
    const targetDates = Array.isArray(result.targetDates) ? result.targetDates.length : 0;
    const createdTasks = Array.isArray(result.createdTasks) ? result.createdTasks.length : 0;
    const updatedTasks = Array.isArray(result.updatedTasks) ? result.updatedTasks.length : 0;
    return [
      'Zwischenreinigungen aktualisiert.',
      `Zimmer: ${bookingRoomLabel(result)}`,
      `Tage: ${targetDates}`,
      `Neu: ${createdTasks}`,
      `Korrigiert: ${updatedTasks}`,
    ].join('\n');
  }

  if (toolName === 'cleaningTask.create') {
    return [
      'Reinigungsaufgabe angelegt.',
      `Buchung: ${String(result.bookingReference || '-')}`,
      `Datum: ${String(result.scheduledDate || '-')}`,
      `Zimmer: ${bookingRoomLabel(result)}`,
    ].join('\n');
  }

  return 'Auftrag ausgeführt.';
}
